import type {
  ExecutionContext,
  SaveSupplierCommand,
  Supplier,
  SupplierRepository,
} from "@dker/core";
import { DomainError } from "@dker/core";
import type { DbClientFactory } from "../ports.js";

/* eslint-disable @typescript-eslint/no-explicit-any */
type Row = Record<string, any>;

function toSupplier(row: Row): Supplier {
  return {
    id: row.id,
    name: row.name,
    contactName: row.contact_name ?? null,
    phone: row.phone ?? null,
    email: row.email ?? null,
    address: row.address ?? null,
    siren: row.siren ?? null,
    notes: row.notes ?? null,
    isActive: row.is_active !== false,
  };
}

function toColumns(command: SaveSupplierCommand): Row {
  return {
    name: command.name,
    contact_name: command.contactName,
    phone: command.phone,
    email: command.email,
    address: command.address,
    siren: command.siren,
    notes: command.notes,
  };
}

function fail(error: { message: string; code?: string }): never {
  throw new DomainError("conflict", `Opération refusée par la base : ${error.message}`, {
    pgCode: error.code,
  });
}

export function createSupabaseSupplierRepository(
  clientFor: DbClientFactory,
): SupplierRepository {
  return {
    async list(ctx: ExecutionContext, includeInactive: boolean): Promise<Supplier[]> {
      let query = clientFor(ctx).from("suppliers").select("*").order("name", { ascending: true });
      if (!includeInactive) query = query.eq("is_active", true);

      const { data, error } = await query;
      if (error) fail(error);
      return (data ?? []).map(toSupplier);
    },

    async save(ctx: ExecutionContext, command: SaveSupplierCommand): Promise<Supplier> {
      const db = clientFor(ctx);
      const columns = toColumns(command);

      const { data, error } = command.supplierId
        ? await db
            .from("suppliers")
            .update(columns)
            .eq("id", command.supplierId)
            .select()
            .maybeSingle()
        : await db.from("suppliers").insert(columns).select().single();

      if (error) fail(error);
      if (!data) {
        throw new DomainError("not_found", "Ce fournisseur n'existe pas.", {
          supplierId: command.supplierId,
        });
      }
      return toSupplier(data);
    },

    async archive(ctx: ExecutionContext, supplierId: string): Promise<void> {
      // Désactivation et non suppression : catalogue et commandes gardent une
      // référence, et un article ne doit pas pointer vers un fournisseur qui
      // n'existe plus.
      const { error } = await clientFor(ctx)
        .from("suppliers")
        .update({ is_active: false })
        .eq("id", supplierId);
      if (error) fail(error);
    },
  };
}
